import { Paragraph, TableTextWrapMode } from '@triumph/design-system';
import { useAppContext } from '@/app/components/providers/AppContextProvider';
import { sumBy } from 'lodash';
import { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import TableCell from './Components/TableCell';
import TableHeader from './Components/TableHeader';
import TableRow from './Components/TableRow';
import { DataTableProps } from './DataTable.types';
import {
    NoRecordsContainer,
    StyledTable,
    TableWrapper,
} from './StyledComponents/TableStyledComponents';

// Pulled from another project, TODO: replace with simplified table from DesignSystem
export const DataTable: React.FC<DataTableProps> = ({
    data,
    columns,
    isShowOverflow,
    width,
    isLoading,
    onSort,
    actionLoadingMessage,
    isHideTotals = false,
    textWrapMode = TableTextWrapMode.Truncate,
    noRecordsHeader,
    noFilteredRecordsMessage,
    isGrouped,
    footerColumns,
    footerData,
    isRowError,
    alternateRowColor,
    maxHeight
}) => {
    const { t } = useTranslation('common');
    const { breakpoint } = useAppContext();

    const tableWrapperRef = useRef() as any;

    const currentColumns = useMemo(() => {
        return columns.filter((c) => c.manuallySelected !== false && !c.hide);
    }, [columns]);

    const headers = useMemo(() => {
        if (currentColumns.length) {
            const isShowTotals =
                !isHideTotals &&
                !isLoading &&
                currentColumns.some(
                    (c) => typeof c.footerAccumlatorFunction === 'function',
                );

            const fieldHeaders = currentColumns
                .filter((c) => c.manuallySelected !== false)
                .map((c, idx) => {
                    return (
                        <TableHeader
                            name={c.header}
                            field={c.field}
                            index={idx}
                            isSortable={c.sortable}
                            key={`${c.field}-${idx}`}
                            handleSort={onSort}
                            textAlignment={c.columnAlignment}
                            renderLabel={c.renderHeaderLabel}
                        />
                    );
                });

            const totals = isShowTotals
                ? currentColumns
                      .filter((c) => c.manuallySelected !== false)
                      .map((c, idx) => {
                          let value = '';
                          if (c.footerAccumlatorFunction) {
                              const sum = sumBy(data, (d: any) => d[c.field]);
                              value = c.footerAccumlatorFunction(sum);
                          }
                          return (
                              <TableHeader
                                  name={idx === 0 ? t('totals') : value}
                                  field={c.field}
                                  index={idx}
                                  isSortable={c.sortable}
                                  key={`${c.field}-${idx}`}
                                  handleSort={onSort}
                                  textAlignment={c.columnAlignment}
                                  isTotal
                              />
                          );
                      })
                : null;

            return (
                <>
                    {isShowTotals && <tr>{totals}</tr>}
                    <tr>{fieldHeaders}</tr>
                </>
            );
        }
        return null;
    }, [currentColumns, window.innerWidth]);

    const renderDataRows = (data: any[]) => {
        const columnNames = currentColumns.map(({ field }) => field);

        return data.map((item, index) => {
            const values = columnNames.map((name, indx) => {
                const val = item[name];
                const col = currentColumns.find(({ field }) => field === name);

                return (
                    <TableCell
                        textWrapMode={textWrapMode}
                        row={item}
                        value={val ?? ''}
                        column={col}
                        key={`results-table-cell-${indx}`}
                    />
                );
            });

            return (
                <TableRow
                    isLoading={isLoading}
                    index={index}
                    key={`results-table-row-${index}`}
                    isError={item.showWarning ?? true}
                    alternateRowColor={alternateRowColor ?? true}
                >
                    {values}
                </TableRow>
            );
        });
    };

    const getDataRows = () => {
        if (data.length) {
            if (!isGrouped) {
                return renderDataRows(data);
            }

            return data.map(({ children, ...rest }) => {
                const values = currentColumns.map(
                    ({ field: columnField, columnAlignment }, index) => {
                        return (
                            <TableHeader
                                name={rest[columnField]}
                                field={columnField}
                                index={index}
                                isSortable={false}
                                key={`${columnField}-${index}`}
                                textAlignment={columnAlignment}
                                isTotal
                            />
                        );
                    },
                );

                return (
                    <>
                        <tr data-testid="parent-data">{values}</tr>
                        {renderDataRows(children)}
                    </>
                );
            });
        }

        return [];
    };

    const getNoResults = () => {
        if (!isLoading && data.length === 0) {
            return (
                <tr>
                    <td colSpan={currentColumns.length}>
                        <NoRecordsContainer
                            breakpoint={breakpoint}
                            isLoading={isLoading}
                        >
                            <Paragraph bold>{noRecordsHeader}</Paragraph>
                            {noFilteredRecordsMessage && (
                                <Paragraph>
                                    {noFilteredRecordsMessage}
                                </Paragraph>
                            )}
                        </NoRecordsContainer>
                    </td>
                </tr>
            );
        }
    };

    const getFooterRows = () => {
        if (footerColumns && footerData && footerData.length) {
            const columnNames = footerColumns.map(({ field }) => field);

            return footerData.map((item, index) => {
                const values = columnNames.map((name, indx) => {
                    const val = item[name];
                    const col = footerColumns.find(
                        ({ field }) => field === name,
                    );

                    return (
                        <TableCell
                            textWrapMode={textWrapMode}
                            row={item}
                            value={val ?? ''}
                            column={col}
                            key={`footer-table-cell-${indx}`}
                        />
                    );
                });

                return (
                    <TableRow
                        isLoading={isLoading}
                        index={index}
                        key={`footer-table-row-${index}`}
                        isError={isRowError}
                        alternateRowColor={alternateRowColor ?? true}
                    >
                        {values}
                    </TableRow>
                );
            });
        }
    };

    return (
        <>
            <TableWrapper
                breakpoint={breakpoint}
                width={width}
                isShowOverflow={isShowOverflow}
                ref={tableWrapperRef}
                hasData={data.length > 0}
                data-testid="data-table-new"
                maxHeight={maxHeight}
            >
                <StyledTable breakpoint={breakpoint} width={width}>
                    <thead>{headers}</thead>
                    <tbody data-testid="data-table-new-body">
                        {getNoResults()}
                        {getDataRows()}
                    </tbody>
                    <tfoot>{getFooterRows()}</tfoot>
                </StyledTable>
            </TableWrapper>
        </>
    );
};
